/**
 * Promo Badge - Signup promotion for anonymous visitors
 * Shows "Sign up to save 15%" badge that opens the newsletter popup
 *
 * The discount code is delivered by email after signup; the theme never applies
 * a discount to the cart. Post-signup the badge points the shopper at their inbox.
 *
 * Edge cases handled:
 * - Different device/browser: shows signup badge, form handles "already subscribed" error
 */
(function () {
  'use strict';

  const STORAGE_KEY = 'promo_signup_complete';
  // When the Klaviyo popup swap is active, CTAs open the Klaviyo form instead of the
  // in-house popup.
  const klaviyoEnabled = !!document.querySelector('meta[name="klaviyo-popups-enabled"]');

  // Safe storage wrappers — WebViews and privacy-mode browsers may return null
  // for `window.localStorage` / `window.sessionStorage`, or throw on access.
  function storageGet(store, key) {
    try {
      return store ? store.getItem(key) : null;
    } catch (_e) {
      return null;
    }
  }
  function storageSet(store, key, value) {
    try {
      if (store) store.setItem(key, value);
    } catch (_e) {
      /* storage unavailable */
    }
  }
  function storageRemove(store, key) {
    try {
      if (store) store.removeItem(key);
    } catch (_e) {
      /* storage unavailable */
    }
  }

  /**
   * Check if user has completed signup (localStorage flag)
   */
  function hasSignedUp() {
    return storageGet(window.localStorage, STORAGE_KEY) === 'true';
  }

  /**
   * Called when signup is complete - update UI. The code is emailed to them.
   */
  function onSignupComplete() {
    storageSet(window.localStorage, STORAGE_KEY, 'true');

    // Update all badges to show activated state
    document.querySelectorAll('promo-badge').forEach((badge) => {
      badge.setActivatedState();
    });
  }

  // Define the custom element
  if (!customElements.get('promo-badge')) {
    customElements.define(
      'promo-badge',
      class PromoBadge extends HTMLElement {
        constructor() {
          super();
          this.productEligible = this.dataset.productEligible === 'true';
          this.variantEligibility = {};
        }

        connectedCallback() {
          // Parse variant eligibility data
          const dataEl = this.querySelector('[data-variant-eligibility]');
          if (dataEl) {
            try {
              this.variantEligibility = JSON.parse(dataEl.textContent);
            } catch (e) {
              // Ignore parse errors
            }
          }

          // Check if already signed up
          if (hasSignedUp()) {
            this.setActivatedState();
            return;
          }

          this.initInteractivity();
          this.reportImpression();
        }

        reportImpression() {
          if (!this.classList.contains('promo-badge--pdp') || this.classList.contains('hidden')) return;
          if (!document.getElementById('LampsCaptureContext')) return;
          const send = () => window.LampsCapture?.impression?.('first_order_15');
          if (window.LampsCapture) send();
          else document.addEventListener('DOMContentLoaded', send, { once: true });
        }

        initInteractivity() {
          // Listen for variant changes — only on PDP, where the variant selector fires
          // them. Cart-drawer badges have a fixed variant; the SSR-rendered state is final.
          // Without this guard, a PDP variant change would also flip cart-drawer badges
          // (their variantEligibility map is empty in cart context, so any variant ID
          // would resolve to false and incorrectly hide the badge).
          if (this.classList.contains('promo-badge--pdp')) {
            this.boundVariantChange = this.onVariantChange.bind(this);
            document.addEventListener('variant:changed', this.boundVariantChange);
          }

          // Make clickable to open popup
          if (this.hasAttribute('aria-controls')) {
            this.addEventListener('click', this.onClick.bind(this));
            this.addEventListener('keydown', this.onKeydown.bind(this));
            this.style.cursor = 'pointer';
          }
        }

        disconnectedCallback() {
          if (this.boundVariantChange) {
            document.removeEventListener('variant:changed', this.boundVariantChange);
          }
        }

        setActivatedState() {
          const activatedText = this.dataset.activatedText;
          if (activatedText) {
            const textEl = this.querySelector('.promo-badge__text');
            if (textEl) {
              textEl.textContent = activatedText;
            }
            // Remove click behavior - no longer interactive
            this.removeAttribute('aria-controls');
            this.removeAttribute('role');
            this.removeAttribute('tabindex');
            this.style.cursor = 'default';
            this.classList.add('promo-badge--activated');
            this.classList.remove('hidden');
          } else {
            // No activated text configured, hide the badge
            this.classList.add('hidden');
          }
        }

        onVariantChange(event) {
          const variantId = event.detail?.variant?.id;
          if (!variantId || !this.productEligible || hasSignedUp()) return;

          const isEligible = this.variantEligibility[variantId] === true;
          this.classList.toggle('hidden', !isEligible);
          this.reportImpression();
        }

        onClick(event) {
          event.preventDefault();
          if (this.classList.contains('promo-badge--activated')) return;

          // With lamps.com capture on, its first-order widget answers the CTA; the paths below are the fallback.
          if (document.getElementById('LampsCaptureContext') && window.LampsCapture?.offer && !event.lampsFallback) {
            window.LampsCapture.offer('first_order_15', () => this.onClick({ preventDefault() {}, lampsFallback: true }));
            return;
          }

          // Klaviyo swap: open the Klaviyo form (tagging this as a shopper-initiated
          // open) instead of the in-house popup.
          if (klaviyoEnabled && window.KlaviyoPopup?.open) {
            window.KlaviyoPopup.open('cta_click');
            return;
          }

          const targetId = this.getAttribute('aria-controls');
          const popup =
            document.getElementById(targetId) || document.querySelector('subscription-popup');
          if (popup?.show) {
            popup.show();
          }
        }

        onKeydown(event) {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            this.onClick(event);
          }
        }
      }
    );
  }

  // Watch for signup success via URL params (form redirect)
  function checkUrlForSuccess() {
    const params = new URLSearchParams(window.location.search);
    return (
      params.has('customer_posted') ||
      (params.get('form_type') === 'customer' && !params.has('contact[email]'))
    );
  }

  // Watch for success message in popup
  function watchForSuccessMessage() {
    const observer = new MutationObserver(() => {
      const successEl = document.querySelector(
        '.subscription-popup .alert--success, .subscription-popup [id*="Newsletter-success"], subscription-popup .alert--success, subscription-popup [id*="Newsletter-success"]'
      );
      if (successEl && !successEl.classList.contains('hidden')) {
        observer.disconnect();
        setTimeout(onSignupComplete, 1500);
      }
    });

    const popup = document.querySelector('subscription-popup');
    if (popup) {
      observer.observe(popup, { childList: true, subtree: true, attributes: true });
    }
  }

  // Watch for "already subscribed" message - returning visitor on a new device
  function watchForAlreadySubscribed() {
    const observer = new MutationObserver(() => {
      const alreadySubscribedEl = document.querySelector(
        'subscription-popup [id*="Newsletter-error-subscribed"], .subscription-popup [id*="Newsletter-error-subscribed"]'
      );
      if (alreadySubscribedEl && !alreadySubscribedEl.classList.contains('hidden')) {
        observer.disconnect();
        setTimeout(onSignupComplete, 1500);
      }
    });

    const popup = document.querySelector('subscription-popup');
    if (popup) {
      observer.observe(popup, { childList: true, subtree: true, attributes: true });
    }
  }

  // Watch for form submission to track pending signup
  function watchFormSubmit() {
    document.addEventListener('submit', (event) => {
      if (
        event.target.matches(
          'subscription-popup form[is="newsletter-form"], subscription-popup [is="newsletter-form"]'
        )
      ) {
        storageSet(window.sessionStorage, 'promo_signup_pending', 'true');
      }
    });
  }

  // Initialize
  function init() {
    if (hasSignedUp()) {
      // Already signed up - badges will handle their own state in connectedCallback
      return;
    }

    // Check if returning from signup redirect
    if (checkUrlForSuccess() && storageGet(window.sessionStorage, 'promo_signup_pending') === 'true') {
      storageRemove(window.sessionStorage, 'promo_signup_pending');
      onSignupComplete();
      return;
    }

    watchForSuccessMessage();
    watchForAlreadySubscribed();
    watchFormSubmit();
    document.addEventListener('lamps-capture:signup', (event) => {
      if (event.detail && event.detail.offer === 'first_order_15') onSignupComplete();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
